import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { Button, Modal } from '@tractian/ui'
import { useTranslation } from 'react-i18next'
import { DeleteIcon } from '@tractian/icons'

import { useCreateAssessment } from '@/features/assessments/api/assessment-create'
import { useDeleteAssessment } from '@/features/assessments/api/assessment-delete'
import type { PlantAssessment } from '@/features/assessments/api/assessment-list'
import {
  useHubspotDuplicates,
  type HubspotSearchItem
} from '@/features/assessments/api/hubspot-search'
import { useUpdateAssessment } from '@/features/assessments/api/assessment-update'
import { usersListQueryOptions } from '@/features/assessments/api/users-list'
import { useFeature } from '@/features/feature-flags/hooks/use-feature'
import { AssessmentForm } from '@/features/assessments/components/assessment-form'
import { HubspotDuplicateWarning } from '@/features/assessments/components/hubspot-duplicate-warning'
import { HubspotPicker } from '@/features/assessments/containers/hubspot-picker'
import { HubspotDealField } from '@/features/assessments/containers/hubspot-deal-field'
import { HubspotPlantField } from '@/features/assessments/containers/hubspot-plant-field'
import {
  getAssessmentFormDefaultValues,
  assessmentFormSchema,
  type AssessmentFormValues
} from '@/features/assessments/domain/assessment-form-schema'
import {
  hubspotPickPlant,
  hubspotPickToFormValues
} from '@/features/assessments/domain/hubspot-pick-to-form'
import { useCreateInstallationPhase } from '@/features/plant-assessment/api/installation-phase-create'
import { toast } from '@/components/primitives/toast'
import { zodResolver } from '@/lib/forms/zod-resolver'

export type AssessmentFormModalProps = {
  open: boolean
  onClose: () => void
  onDeleted?: () => void
  mode: 'create' | 'edit'
  assessment?: PlantAssessment
}

// Create with the HubSpot flags on starts on the "pick" step; skipping it or
// continuing with a pick moves to the regular form.
type Step = 'pick' | 'form'

export function AssessmentFormModal({
  open,
  onClose,
  onDeleted,
  mode,
  assessment
}: AssessmentFormModalProps) {
  const { t } = useTranslation('plantAssessment')
  const multiPhase = useFeature('multiPhase')
  const hubspotSearch = useFeature('hubspotAssessmentSearch')
  const showHubspotPicker = mode === 'create' && multiPhase && hubspotSearch
  const initialStep: Step = showHubspotPicker ? 'pick' : 'form'
  const [step, setStep] = useState<Step>(initialStep)
  const [hubspotPick, setHubspotPick] = useState<HubspotSearchItem | null>(null)
  // The Deal ID for the "Pilot" phase created after submit; null for a plant pick or a skip.
  const [pilotDealId, setPilotDealId] = useState<string | null>(null)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { data: users = [] } = useQuery({
    ...usersListQueryOptions(),
    enabled: open
  })
  const userOptions = users.map(u => ({ value: u.id, label: u.name }))

  const form = useForm<AssessmentFormValues>({
    resolver: zodResolver(assessmentFormSchema),
    defaultValues: getAssessmentFormDefaultValues(mode, assessment)
  })

  // The modal stays mounted while closed (callers key it by assessment id, so
  // reopening the same assessment does not remount), and useForm never
  // re-reads defaultValues. Re-sync from the current assessment when the
  // modal opens — and whenever the prop refreshes while the form is pristine
  // — so a value saved in a previous session (e.g. the HubSpot Plant ID)
  // shows up instead of the stale defaults from the last close. The dirty
  // guard keeps a background refetch from clobbering in-progress edits.
  useEffect(() => {
    if (open && !form.formState.isDirty) {
      form.reset(getAssessmentFormDefaultValues(mode, assessment))
      setHubspotPick(null)
      setPilotDealId(null)
      setStep(initialStep)
    }
  }, [open, mode, assessment, form, initialStep])

  // On the pick step the lookup follows the pick; on the form it follows the
  // pick's deal and whatever plant the HubSpot Plant field holds now.
  const formPlantId = useWatch({ control: form.control, name: 'hubspotPlantId' })
  const duplicates = useHubspotDuplicates(
    showHubspotPicker ? duplicateLookup(step, hubspotPick, pilotDealId, formPlantId) : {}
  )
  // The warning is a hint, never a blocker: on failure the banner just stays hidden.
  useEffect(() => {
    if (duplicates.isError) {
      console.error('[assessment-form] HubSpot duplicates lookup failed:', duplicates.error)
    }
  }, [duplicates.isError, duplicates.error])
  const duplicateWarning = duplicates.isSuccess && duplicates.data.length > 0 && (
    <HubspotDuplicateWarning duplicates={duplicates.data} />
  )

  const createMutation = useCreateAssessment()
  const updateMutation = useUpdateAssessment()
  const deleteMutation = useDeleteAssessment()
  const createPhaseMutation = useCreateInstallationPhase()
  const isPending =
    createMutation.isPending ||
    createPhaseMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending

  function closeModal() {
    form.reset(getAssessmentFormDefaultValues(mode, assessment))
    setHubspotPick(null)
    setPilotDealId(null)
    setStep(initialStep)
    setShowDiscardConfirm(false)
    setShowDeleteConfirm(false)
    onClose()
  }

  function confirmDelete() {
    if (!assessment) return
    deleteMutation.mutate(assessment.id, {
      onSuccess: () => {
        closeModal()
        onDeleted?.()
      }
    })
  }

  function requestClose() {
    if (form.formState.isDirty) {
      setShowDiscardConfirm(true)
    } else {
      closeModal()
    }
  }

  function skipHubspotPick() {
    setHubspotPick(null)
    setPilotDealId(null)
    setStep('form')
  }

  // The form is still pristine on the pick step, so the pick simply becomes the
  // form defaults: autofilled values do not count as edits and closing right
  // away asks no discard confirmation.
  function continueWithHubspotPick() {
    if (!hubspotPick) return
    const { values, dealId } = hubspotPickToFormValues(hubspotPick)
    form.reset({ ...getAssessmentFormDefaultValues(mode, assessment), ...values })
    setPilotDealId(dealId)
    setStep('form')
  }

  // A deal pick gets its first installation phase right after the assessment
  // exists. No name is sent, so the API names it "Pilot". A failure keeps the
  // assessment and only warns: the phase can still be added from the panel.
  async function createPilotPhase(plantAssessmentId: string, hubspotDealId: string) {
    try {
      await createPhaseMutation.mutateAsync({ plantAssessmentId, hubspotDealId })
    } catch {
      toast.error(t('assessmentFormModal.pilotPhaseError'))
    }
  }

  function onSubmit(values: AssessmentFormValues) {
    // With multiPhase on, the assessment carries the HubSpot Plant ID and the
    // deal reference is owned by the installation phases — never send
    // hubspotDealId in that shape.
    if (mode === 'create') {
      const dealId = showHubspotPicker ? pilotDealId : null
      createMutation.mutate(
        {
          plantName: values.plantName,
          country: values.country,
          // Create takes a plain string for both owners: omit rather than send null.
          ...(values.dealOwnerUserID ? { dealOwnerUserID: values.dealOwnerUserID } : {}),
          ...(values.salesEngineerUserID
            ? { salesEngineerUserID: values.salesEngineerUserID }
            : {}),
          isAtex: values.isAtex,
          ...(multiPhase
            ? { hubspotPlantId: values.hubspotPlantId }
            : { hubspotDealId: values.hubspotDealId })
        },
        {
          onSuccess: async created => {
            if (dealId) await createPilotPhase(created.id, dealId)
            closeModal()
          }
        }
      )
      return
    }

    if (assessment) {
      updateMutation.mutate(
        {
          id: assessment.id,
          plantName: values.plantName,
          country: values.country,
          dealOwnerUserID: values.dealOwnerUserID ?? null,
          salesEngineerUserID: values.salesEngineerUserID ?? null,
          isAtex: values.isAtex,
          ...(multiPhase
            ? { hubspotPlantId: values.hubspotPlantId }
            : { hubspotDealId: values.hubspotDealId }),
          assessedAt: values.assessedAt ? new Date(values.assessedAt).toISOString() : null,
          lastOnsiteAt: values.lastOnsiteAt ? new Date(values.lastOnsiteAt).toISOString() : null
        },
        {
          onSuccess: closeModal
        }
      )
    }
  }

  if (!open) return null

  const formTitle =
    mode === 'create' ? t('assessmentFormModal.title.create') : t('assessmentFormModal.title.edit')
  const title = step === 'pick' ? t('hubspotPicker.title') : formTitle

  return (
    <>
      <Modal
        open={open}
        onOpenChange={nextOpen => {
          if (!nextOpen) requestClose()
        }}
      >
        <Modal.Content classes={{ content: 'h-fit w-[500px] max-w-[100vw]' }}>
          <Modal.Header>
            <div className="flex items-center justify-between">
              <span>{title}</span>
              {mode === 'edit' && (
                <Button
                  type="button"
                  variant="link"
                  size="small"
                  aria-label={t('assessmentFormModal.deleteAction')}
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isPending}
                >
                  <DeleteIcon className="text-red-500" width={16} height={16} />
                </Button>
              )}
            </div>
          </Modal.Header>
          <Modal.Body>
            {step === 'pick' ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-slate-600">{t('hubspotPicker.description')}</p>
                <HubspotPicker value={hubspotPick} onChange={setHubspotPick} autoFocus />
                {duplicateWarning}
              </div>
            ) : (
              <>
                <AssessmentForm
                  form={form}
                  userOptions={userOptions}
                  onSubmit={onSubmit}
                  mode={mode}
                  multiPhase={multiPhase}
                  renderHubspotDealInput={props => (
                    <HubspotDealField {...props} disabled={isPending} />
                  )}
                  renderHubspotPlantInput={props => (
                    <HubspotPlantField
                      {...props}
                      knownPlant={hubspotPick ? hubspotPickPlant(hubspotPick) : null}
                      disabled={isPending}
                    />
                  )}
                />
                {duplicateWarning && <div className="mt-4">{duplicateWarning}</div>}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {/* Distinct keys: without them React reuses Continue's <button> as the
                type="submit" Create button mid-click, and the click submits the form. */}
            {step === 'pick' ? (
              <Modal.FooterActions key="pick-actions">
                <Button type="button" variant="outline" onClick={skipHubspotPick}>
                  {t('hubspotPicker.skip')}
                </Button>
                <Button type="button" disabled={!hubspotPick} onClick={continueWithHubspotPick}>
                  {t('hubspotPicker.continue')}
                </Button>
              </Modal.FooterActions>
            ) : (
              <Modal.FooterActions key="form-actions">
                <Button type="button" variant="outline" onClick={requestClose} disabled={isPending}>
                  {t('assessmentFormModal.cancel')}
                </Button>
                <Button type="submit" form="assessment-form" loading={isPending}>
                  {mode === 'create'
                    ? t('assessmentFormModal.submit.create')
                    : t('assessmentFormModal.submit.edit')}
                </Button>
              </Modal.FooterActions>
            )}
          </Modal.Footer>
        </Modal.Content>
      </Modal>

      <Modal open={showDiscardConfirm} onOpenChange={v => !v && setShowDiscardConfirm(false)}>
        <Modal.Content classes={{ content: 'h-fit w-[500px] max-w-[100vw]' }}>
          <Modal.Header>{t('assessmentFormModal.discardDialog.title')}</Modal.Header>
          <Modal.Body>
            <p>{t('assessmentFormModal.discardDialog.body')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Modal.FooterActions>
              <Button type="button" variant="outline" onClick={() => setShowDiscardConfirm(false)}>
                {t('assessmentFormModal.discardDialog.keepEditing')}
              </Button>
              <Button type="button" danger onClick={closeModal}>
                {t('assessmentFormModal.discardDialog.discard')}
              </Button>
            </Modal.FooterActions>
          </Modal.Footer>
        </Modal.Content>
      </Modal>

      <Modal open={showDeleteConfirm} onOpenChange={v => !v && setShowDeleteConfirm(false)}>
        <Modal.Content classes={{ content: 'h-fit w-[500px] max-w-[100vw]' }}>
          <Modal.Header>{t('assessmentFormModal.deleteDialog.title')}</Modal.Header>
          <Modal.Body>
            <p>{t('assessmentFormModal.deleteDialog.body')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Modal.FooterActions>
              <Button type="button" variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                {t('assessmentFormModal.deleteDialog.cancel')}
              </Button>
              <Button
                type="button"
                danger
                loading={deleteMutation.isPending}
                onClick={confirmDelete}
              >
                {t('assessmentFormModal.deleteDialog.confirm')}
              </Button>
            </Modal.FooterActions>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </>
  )
}

function duplicateLookup(
  step: Step,
  pick: HubspotSearchItem | null,
  pilotDealId: string | null,
  formPlantId: string | null
) {
  if (step === 'form') return { dealId: pilotDealId, plantId: formPlantId }
  if (!pick) return {}
  const { values, dealId } = hubspotPickToFormValues(pick)
  return { dealId, plantId: values.hubspotPlantId ?? null }
}
